import React, { useState, useEffect, useRef } from "react";
import Box from "@mui/material/Box";
import Select from "@mui/material/Select";
import InputLabel from '@mui/material/InputLabel';
import FormControl from '@mui/material/FormControl';
import MenuItem from "@mui/material/MenuItem";
import MicIcon from '@mui/icons-material/Mic';
import VolumeUpIcon from '@mui/icons-material/VolumeUp';
import Button from '@mui/material/Button';

const AudioSettings = ({ selectedAudioTypeDevice, setSelectedAudioTypeDevice, audioType }) => {
  const [temporalAudioDevice, setTemporalAudioDevice] = useState(null);
  const [audioDevices, setAudioDevices] = useState([]);
  const isMicrophone = audioType === "audioinput";
  const audioTitle = isMicrophone ? "Microphone" : "Speaker";

  const microphoneFrequencyBarRef = useRef(null);
  const analyzerRef = useRef(null);
  const mediaStreamRef = useRef(null);

  useEffect(() => {
    // Fetch available audio input devices using navigator.mediaDevices
    if (navigator.mediaDevices && navigator.mediaDevices.enumerateDevices) {
      navigator.mediaDevices.enumerateDevices().then((devices) => {
        const audioInputDevices = devices.filter(
          (device) => device.kind === audioType
        );
        setAudioDevices(audioInputDevices);

        // Set the default audio device if available
        const defaultDevice = audioInputDevices.find(
          (device) => device.deviceId === selectedAudioTypeDevice
        );
        if (defaultDevice) {
          setTemporalAudioDevice(selectedAudioTypeDevice);
        } else if (audioInputDevices.length > 0) {
          setTemporalAudioDevice(audioInputDevices[0].deviceId); // Set the first device as default
        }
      });
    }
  }, [audioType, selectedAudioTypeDevice]);

  const handleSubmit = (deviceIdValue) => {
    setTemporalAudioDevice(deviceIdValue)
    setSelectedAudioTypeDevice(deviceIdValue);
  };

  useEffect(() => {
    if (temporalAudioDevice) {
      if (isMicrophone) {
        // Start analyzing microphone input
        startFrequencyAnalysis();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [temporalAudioDevice]);

  const startFrequencyAnalysis = async () => {
    try {
      mediaStreamRef.current = await navigator.mediaDevices.getUserMedia({ audio: { deviceId: temporalAudioDevice } });
      const audioContext = new AudioContext();
      const source = audioContext.createMediaStreamSource(mediaStreamRef.current);
      analyzerRef.current = audioContext.createAnalyser();
      source.connect(analyzerRef.current);
      analyzerRef.current.fftSize = 2048;

      // Start updating the frequency bar graph
      updateFrequencyBars();
    } catch (error) {
      console.error('Error starting frequency analysis:', error);
    }
  };

  const updateFrequencyBars = () => {
    const canvas = microphoneFrequencyBarRef.current;
    const ctx = canvas.getContext('2d');

    const bufferLength = analyzerRef.current.frequencyBinCount;
    const dataArray = new Uint8Array(bufferLength);

    const barWidth = (canvas.width / bufferLength) * 2.5;
    const barSpacing = 2;
    const barHeightMultiplier = canvas.height / 256; // Adjust for the range of frequency data

    const draw = () => {
      requestAnimationFrame(draw);

      analyzerRef.current.getByteFrequencyData(dataArray);

      ctx.fillStyle = 'white';
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      let x = 0;
      for (let i = 0; i < bufferLength; i++) {
        const barHeight = dataArray[i] * barHeightMultiplier;

        ctx.fillStyle = 'red';
        ctx.fillRect(x, canvas.height - barHeight, barWidth, barHeight);

        x += barWidth + barSpacing;
      }
    };

    draw();
  };

  const playTestSound = async () => {
    try {
      // Create a new audio context
      const audioContext = new AudioContext();

      // Create a gain node to control the volume
      const gainNode = audioContext.createGain();

      // Connect the gain node to the destination
      gainNode.connect(audioContext.destination);

      // Create an oscillator
      const oscillator = audioContext.createOscillator();

      // Connect the oscillator to the gain node
      oscillator.connect(gainNode);

      // Set oscillator type and frequency
      oscillator.type = "sine";
      oscillator.frequency.setValueAtTime(440, audioContext.currentTime); // A4 note frequency

      // Start the oscillator
      oscillator.start();

      // Stop the oscillator after 1 second
      oscillator.stop(audioContext.currentTime + 1);

      // Get the selected audio output device
      const device = await navigator.mediaDevices.getDeviceById(selectedAudioTypeDevice);

      // Create a media stream destination node
      const destination = audioContext.createMediaStreamDestination();

      // Set the destination device for the media stream
      await audioContext.audioWorklet.addModule('worklet.js');

      // Connect the gain node to the media stream destination
      gainNode.connect(destination);

      // Create a media stream from the destination node
      const mediaStream = destination.stream;

      // Set the output device for the media stream
      await device.applyConstraints({
        audio: {
          deviceId: { exact: selectedAudioTypeDevice }
        }
      });

      // Set the media stream as the source of the audio output device
      const audioElement = new Audio();
      audioElement.srcObject = mediaStream;
      audioElement.play();
    } catch (error) {
      console.error('Error playing test sound:', error);
    }
  };

  if (audioDevices.length === 0) {
    return <div>No audio devices available</div>;
  }

  return (
    <Box component="form" noValidate autoComplete="off">
      <Box sx={{ display: 'flex', alignItems: 'flex-end' }}>
        <FormControl fullWidth>
          <InputLabel id="demo-simple-select-label">{audioTitle}</InputLabel>
          <Select
            id={audioType}
            label={audioTitle}
            value={temporalAudioDevice}
            onChange={(e) => handleSubmit(e.target.value)}
            variant="outlined"
            fullWidth
          >
            {audioDevices.map((device) => (
              <MenuItem key={device.deviceId} value={device.deviceId}>
                {/* Rendering MicIcon or Speaker alongside the text */}
                <span style={{ display: 'flex', alignItems: 'center' }}>
                  <span style={{ marginRight: '5px', width: '25px', position: 'relative', top: '3px' }}>
                    {device.deviceId === selectedAudioTypeDevice && (audioTitle === "Microphone" ? <MicIcon fontSize="small" /> : <VolumeUpIcon fontSize="small" />)}
                  </span>
                  <span>
                    {device.deviceId === selectedAudioTypeDevice
                      ? `${device.label || `Device ${device.deviceId}`}`
                      : device.label || `Device ${device.deviceId}`}
                  </span>
                </span>
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {!isMicrophone && (
          <div style={{ marginLeft: '10px', width: '50px', }}>
            <Button variant="outlined" onClick={playTestSound}>Test</Button>
          </div>
        )}

        {isMicrophone ? <canvas ref={microphoneFrequencyBarRef} width={50} height={50} style={{ marginLeft: '10px' }} /> : null}
      </Box>
    </Box>
  );
};

export default AudioSettings;
